import { lastValueFrom } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';

import { PORTAL_API_BASE_URL } from '@echospaces-web-monorepo/es-portal-shell';

export interface Space {
  id: string;
  name: string;
  slug: string;
  description?: string;
  spaceType: 'island' | 'hex' | 'building' | 'floor' | 'room' | 'open_space';
  islandId: string | null;
  logo: string | null;
  order: number;
  parent: Space | null;
  children: Space[];
  createdAt: string;
  updatedAt: string;
}

/**
 * Mutable base fields on a Space. Matches the server's UpdateSpaceDto:
 * polymorphic across every concrete Space type (island, building, room…).
 */
export interface UpdateSpaceDto {
  name?: string;
  description?: string | null;
  logo?: string | null;
  order?: number;
}

/**
 * Manages the spatial tree and selected space state for the CMS admin UI.
 * Fetches island buildings and individual space details from the admin API.
 */
@Injectable({ providedIn: 'root' })
export class SpaceService {
  private readonly http = inject(HttpClient);
  private readonly api = inject(PORTAL_API_BASE_URL);

  private readonly _spaces = signal<Space[]>([]);
  private readonly _selectedSpace = signal<Space | null>(null);
  private readonly _loading = signal(false);

  readonly spaces = this._spaces.asReadonly();
  readonly selectedSpace = this._selectedSpace.asReadonly();
  readonly loading = this._loading.asReadonly();

  async loadSpaces(islandId: string): Promise<void> {
    this._loading.set(true);
    try {
      // The island's root space IS addressed by the island id (root
      // IslandSpace.id === catalog Island.id). Fetch it with its children as the
      // tree root — the tree renders `spaces()` as top-level nodes and recurses
      // into each node's `children`.
      const root = await lastValueFrom(
        this.http.get<Space>(`${this.api}/ecms/spaces/${islandId}?includeChildren=true`),
      );
      this._spaces.set([root]);
    } finally {
      this._loading.set(false);
    }
  }

  async loadSpace(spaceId: string): Promise<void> {
    this._loading.set(true);
    try {
      const data = await lastValueFrom(this.http.get<Space>(`${this.api}/ecms/spaces/${spaceId}?includeChildren=true&includeParent=true`));
      this._selectedSpace.set(data);
    } finally {
      this._loading.set(false);
    }
  }

  selectSpace(space: Space | null): void {
    this._selectedSpace.set(space);
  }

  /**
   * Patch base metadata on a Space. PATCH semantics — only the fields
   * present in `dto` are touched. Updates the cached `selectedSpace`
   * with the server response so the detail view reflects the new state
   * without an extra GET.
   */
  async updateSpace(spaceId: string, dto: UpdateSpaceDto): Promise<Space> {
    const updated = await lastValueFrom(
      this.http.patch<Space>(`${this.api}/ecms/spaces/${spaceId}`, dto),
    );
    if (this._selectedSpace()?.id === spaceId) {
      this._selectedSpace.set(updated);
    }
    return updated;
  }
}
